// src/services/api.js
import axios from "axios";
import i18n from "../i18n";
import { buildApiUrl } from "../config/urls";
import { getCountryGateState } from "../utils/countryGateState";
import { getStoredSelectedCountry, normalizeCountryId } from "../utils/country";
import { backendLangFromI18n } from "../utils/lang";

/* ------------------------------ HELPERS ---------------------------------- */

function toApiLang(lang) {
  if (lang === "el") return "gr";
  return lang || "en";
}

const GUEST_ID_KEY = "incooking_guest_id";
const GUEST_TOKEN_KEY = "incooking_guest_token";
const AUTH_TOKEN_KEY = "auth_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const LOCATION_STATE_KEY = "incooking_location_v1";
let _guestIdMemory = null;
let _guestTokenMemory = null;
let _refreshPromise = null;

const getAuthTokenFromPayload = (payload = {}) =>
  payload?.auth_token || payload?.token || payload?.access_token || payload?.jwt || null;

const getRefreshTokenFromPayload = (payload = {}) =>
  payload?.refresh_token || null;

const persistAuthToken = (token) => {
  const value = String(token || "").trim();
  if (!value) return false;
  try {
    localStorage.setItem(AUTH_TOKEN_KEY, value);
  } catch (_err) {
    return false;
  }
  axios.defaults.headers.common.Authorization = `Bearer ${value}`;
  return true;
};

const persistRefreshToken = (token) => {
  const value = String(token || "").trim();
  if (!value) return false;
  try {
    localStorage.setItem(REFRESH_TOKEN_KEY, value);
    return true;
  } catch (_err) {
    return false;
  }
};

const persistSessionTokens = (payload = {}) => {
  const accessStored = persistAuthToken(getAuthTokenFromPayload(payload));
  const refreshToken = getRefreshTokenFromPayload(payload);
  let refreshStored = false;
  if (refreshToken) {
    refreshStored = persistRefreshToken(refreshToken);
  } else {
    try {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } catch (_err) {}
  }
  return accessStored || refreshStored;
};

const clearAuthToken = () => {
  try {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  } catch (_err) {}
  delete axios.defaults.headers.common.Authorization;
};

(() => {
  try {
    const existing = localStorage.getItem(AUTH_TOKEN_KEY);
    if (existing) {
      axios.defaults.headers.common.Authorization = `Bearer ${existing}`;
    }
  } catch (_err) {}
})();

function getGuestId() {
  if (_guestIdMemory) return _guestIdMemory;
  try {
    const fromStorage = localStorage.getItem(GUEST_ID_KEY) || null;
    if (fromStorage) _guestIdMemory = fromStorage;
    return fromStorage;
  } catch {
    return _guestIdMemory || null;
  }
}
function setGuestId(id) {
  _guestIdMemory = id ? String(id) : null;
  try {
    localStorage.setItem(GUEST_ID_KEY, id);
  } catch { }
}

function getGuestToken() {
  if (_guestTokenMemory) return _guestTokenMemory;
  try {
    const fromStorage = localStorage.getItem(GUEST_TOKEN_KEY) || null;
    if (fromStorage) _guestTokenMemory = fromStorage;
    return fromStorage;
  } catch {
    return _guestTokenMemory || null;
  }
}
function setGuestToken(token) {
  _guestTokenMemory = token ? String(token) : null;
  try {
    if (token) localStorage.setItem(GUEST_TOKEN_KEY, token);
  } catch { }
}

let _guestInitPromise = null;

// ✅ SINGLE-FLIGHT: ποτέ δύο /guest/start μαζί
async function initGuestOnce(apiUsersInstance) {
  if (_guestInitPromise) return _guestInitPromise;

  _guestInitPromise = (async () => {
    const existing = getGuestId();
    const res = await apiUsersInstance.post(
      "/guest/start",
      existing ? { guest_id: existing } : {}
    );
    const gid = res?.data?.guest_id || existing || null;
    const gtoken = res?.data?.guest_token || null;
    if (gid) setGuestId(gid);
    if (gtoken) setGuestToken(gtoken);
    if (process.env.REACT_APP_DEBUG_GUEST_STATE === "1") {
      console.info("[guest] start", {
        gid,
        hasToken: !!gtoken,
        hasCookieHeader: !!res?.headers?.["set-cookie"],
        storageReadable: (() => {
          try {
            return typeof window !== "undefined" && !!window.localStorage;
          } catch {
            return false;
          }
        })(),
      });
    }
    return gid;
  })().catch((e) => {
    console.error("[guest] /guest/start failed:", e?.response?.data || e.message);
    _guestInitPromise = null;
    throw e;
  });

  return _guestInitPromise;
}

// ✅ αυτό ζητάει το export σου — άρα φεύγει το error
async function ensureGuestId() {
  const gid = getGuestId() || _guestIdMemory;
  if (gid) return gid;
  // θα δημιουργηθεί από initGuestOnce μόλις υπάρχει axios instance
  // (θα το καλέσουμε από guestAPI / interceptor)
  return null;
}

function getStoredRefreshToken() {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

async function refreshAccessToken() {
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) {
    throw new Error("Missing refresh token");
  }
  if (!_refreshPromise) {
    _refreshPromise = api_users
      .post("/refresh", { refresh_token: refreshToken })
      .then((response) => {
        persistSessionTokens(response?.data || {});
        return response?.data || {};
      })
      .catch((error) => {
        clearAuthToken();
        throw error;
      })
      .finally(() => {
        _refreshPromise = null;
      });
  }
  return _refreshPromise;
}

function getCurrentCountryId() {
  const stored = getStoredSelectedCountry();
  if (stored?.id) return Number(stored.id);
  try {
    const raw = localStorage.getItem(LOCATION_STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    const normalized = normalizeCountryId(parsed?.country);
    if (normalized) return Number(normalized);
  } catch (_err) {}
  return 1;
}



const createApiInstance = (baseURL) => {
  const resolvedBaseURL = String(baseURL || "/api").replace(/\/+$/, "");
  const instance = axios.create({
    baseURL: resolvedBaseURL,
    withCredentials: true,
    headers: { "Content-Type": "application/json" },
  });

  instance.interceptors.request.use(
    async (config) => {
      const needsCountrySelection = getCountryGateState();
      if (needsCountrySelection) {
        const method = (config.method || "get").toLowerCase();
        const base = String(instance.defaults.baseURL || "").replace(/\/+$/, "");
        const url = String(config.url || "");
        const path = `${base}${url.startsWith("/") ? "" : "/"}${url}`.replace(/\/+$/, "");

        const isGuestEndpoint = url.startsWith("/guest/");
        const guardedPaths = [
          "/stores/search",
          "/shopping_list_preview/preview_for_recipe",
          "/shopping_list_preview/preview_custom",
          "/shopping_list_preview/recalculate",
          "/shopping_list_preview/preview_from_mealplan",
          "/shopping_list/preview_from_meal_plan",
          "/meal_plan/price_recipe",
        ];

        const isShoppingListList =
          method === "get" && /\/shopping_list\/?$/.test(path);

        const matchesGuarded =
          guardedPaths.some((p) => path.endsWith(p)) || isShoppingListList;

        if (!isGuestEndpoint && matchesGuarded) {
          const err = new Error("COUNTRY_REQUIRED");
          err.code = "COUNTRY_REQUIRED";
          err.isCountryGate = true;
          err.config = config;
          return Promise.reject(err);
        }
      }

      // lang query param for ALL requests
      let currentLang = i18n.language || "en";
      if (currentLang === "el") currentLang = "gr";
      config.params = { lang: currentLang, ...(config.params || {}) };

      // auth token
      const token = localStorage.getItem(AUTH_TOKEN_KEY);
      if (token) config.headers.Authorization = `Bearer ${token}`;

      // ensure guest session for POST (avoid recursion on /guest/*)
      if (config.method && config.method.toLowerCase() === "post") {
        const url = String(config.url || "");
        const isUsersGuestEndpoint = url.startsWith("/guest/"); // /guest/start, /guest/state, /guest/claim

        if (!isUsersGuestEndpoint) {
          try {
            await initGuestOnce(api_users);
          } catch {
            // ignore
          }
        }
      }

      // Always include guest header when available as a robust fallback
      // for cross-origin cookie drops between apex and www hosts.
      const gid = getGuestId();
      if (gid) config.headers["X-Guest-Id"] = gid;

      const gtoken = getGuestToken();
      if (gtoken) config.headers["X-Guest-Token"] = gtoken;

      return config;
    },
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error?.code === "COUNTRY_REQUIRED" || error?.isCountryGate) {
        return Promise.reject(error);
      }
      const status = error?.response?.status;
      const data = error?.response?.data;
      const originalRequest = error?.config;
      const requestUrl = String(originalRequest?.url || "");
      const isAuthRoute = ["/login", "/register", "/refresh", "/logout", "/logout_all"].some((path) =>
        requestUrl.endsWith(path)
      );
      const hasAuthToken = Boolean(getAuthTokenFromPayload({ auth_token: localStorage.getItem(AUTH_TOKEN_KEY) }));
      if (
        status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !isAuthRoute &&
        hasAuthToken
      ) {
        originalRequest._retry = true;
        return refreshAccessToken().then((refreshed) => {
          const nextAccessToken = getAuthTokenFromPayload(refreshed);
          if (nextAccessToken) {
            originalRequest.headers = {
              ...(originalRequest.headers || {}),
              Authorization: `Bearer ${nextAccessToken}`,
            };
          }
          return instance(originalRequest);
        });
      }
      console.error(
        `[API ${instance.defaults.baseURL}]`,
        "status:",
        status,
        "error:",
        data || error.message
      );
      return Promise.reject(error);
    }
  );

  return instance;
};

/* ---------------------------- AXIOS INSTANCES ---------------------------- */
/* NOTE:
   - Users:            /api/users
   - Shopping list:    /api/shopping_list
   - Preview:          /api/shopping_list_preview
   - Instacart:        /api/instacart
*/

const api_users = createApiInstance(
  process.env.REACT_APP_USERS_ROUTES_API_BASE_URL || buildApiUrl("/users")
);

const api_household = createApiInstance(
  process.env.REACT_APP_HOUSEHOLD_ROUTES_API_BASE_URL || buildApiUrl("/household")
);

const api_recipes = createApiInstance(
  process.env.REACT_APP_RECIPES_ROUTES_API_BASE_URL || buildApiUrl("/recipes")
);

const api_inventory = createApiInstance(
  process.env.REACT_APP_INVENTORY_ROUTES_API_BASE_URL || buildApiUrl("/inventory")
);

const api_meal_plan = createApiInstance(
  process.env.REACT_APP_MEAL_PLAN_ROUTES_API_BASE_URL || buildApiUrl("/meal_plan")
);

// ✅ CRUD / lists
const api_shopping_list = createApiInstance(
  process.env.REACT_APP_SHOPPING_ROUTES_API_BASE_URL || buildApiUrl("/shopping_list")
);

// ✅ preview / costing
const api_shopping_preview = createApiInstance(
  process.env.REACT_APP_SHOPPING_PREVIEW_API_BASE_URL || buildApiUrl("/shopping_list_preview")
);

// ✅ instacart
const api_instacart = createApiInstance(
  process.env.REACT_APP_INSTACART_API_BASE_URL || buildApiUrl("/instacart")
);

const api_products = createApiInstance(
  process.env.REACT_APP_PRODUCTS_ROUTES_API_BASE_URL || buildApiUrl("/products")
);

const api_pricing = createApiInstance(
  process.env.REACT_APP_PRICING_ROUTES_API_BASE_URL || buildApiUrl("/pricing")
);

const api_generic_products = createApiInstance(
  process.env.REACT_APP_GENERIC_PRODUCTS_ROUTES_API_BASE_URL || buildApiUrl("/generic_products")
);

const api_variants = createApiInstance(
  process.env.REACT_APP_VARIANT_PRODUCTS_API_BASE_URL || buildApiUrl("/variant_products")
);

const api_favorites = createApiInstance(
  process.env.REACT_APP_FAVORITES_ROUTES_API_BASE_URL || buildApiUrl("/favorites")
);

const api_stripe = createApiInstance(
  process.env.REACT_APP_STRIPE_ROUTES_API_BASE_URL || buildApiUrl("/stripe")
);
const api_public = createApiInstance(
  process.env.REACT_APP_PUBLIC_API_BASE_URL || buildApiUrl("/public")
);
const api_price = createApiInstance(
  process.env.REACT_APP_PRICE_API_BASE_URL || buildApiUrl("/price")
);
const api_smart_kitchen = createApiInstance(
  process.env.REACT_APP_SMART_KITCHEN_API_BASE_URL || buildApiUrl("/smart_kitchen")
);
const api_followed_products = createApiInstance(
  process.env.REACT_APP_FOLLOWED_PRODUCTS_API_BASE_URL || buildApiUrl("/followed_products")
);
const api_followed_generic = createApiInstance(
  process.env.REACT_APP_FOLLOWED_GENERIC_API_BASE_URL || buildApiUrl("/followed_generic")
);
const api_shopping_lists = createApiInstance(
  // TODO(deprecation): this base URL is for temporary /api/shopping_lists/*
  // compatibility routes that are outside the canonical shopping-list contract.
  process.env.REACT_APP_SHOPPING_LISTS_ALIAS_API_BASE_URL || buildApiUrl("/shopping_lists")
);

// ✅ App Reviews (Feedback) base URL (σωστό env)
const api_reviews = createApiInstance(
  process.env.REACT_APP_APPREVIEWS_ROUTES_API_BASE_URL || buildApiUrl("/app_reviews")
);



const api_recipe_reviews = createApiInstance(
  process.env.REACT_APP_RECIPES_REVIEWS_ROUTES_API_BASE_URL || buildApiUrl("/recipe_reviews")
);

const api_stores = createApiInstance(
  process.env.REACT_APP_STORES_ROUTES_API_BASE_URL || buildApiUrl("/stores")
);

const api_kroger = createApiInstance(
  process.env.REACT_APP_KROGER_API_BASE_URL || buildApiUrl("/")
);

const api_guest = createApiInstance(
  process.env.REACT_APP_GUEST_ROUTES_API_BASE_URL || buildApiUrl("/users")
);

const api_guest_meal_plan = createApiInstance(
  process.env.REACT_APP_GUEST_MEAL_PLAN_ROUTES_API_BASE_URL || buildApiUrl("/guest")
);

const api_feedback = createApiInstance(
  process.env.REACT_APP_FEEDBACK_API_BASE_URL || buildApiUrl("/")
);
const api_admin = createApiInstance(
  process.env.REACT_APP_ADMIN_API_BASE_URL || buildApiUrl("/admin")
);

/* ------------------------------ FEEDBACK API ------------------------------ */

const feedbackAPI = {
  submit: async ({ category, message, page_url, email = null, pricing_intent = null }) => {
    // εξασφάλισε guest session (αν δεν είναι logged)
    await initGuestOnce(api_users);

    return api_feedback.post("/feedback", {
      category,
      message,
      page_url,
      email,
      pricing_intent,
      browser: navigator.userAgent,
    });
  },
};

/* ------------------------------ GUEST API -------------------------------- */

const guestAPI = {
  start: async () => {
    const gid = await initGuestOnce(api_users);
    return { data: { guest_id: gid, ok: true } };
  },

  mergeState: async (partialState) => {
    const gid = await initGuestOnce(api_guest);
    return api_guest.post(`/guest/state`, partialState || {}, {
      params: { guest_id: gid },
    });
  },

  getState: async () => {
    const gid = await initGuestOnce(api_guest);
    return api_guest.get(`/guest/state`, { params: { guest_id: gid } });
  },

  claim: (guest_id, local_state) =>
    api_guest.post("/guest/claim", { guest_id, local_state }),
};

/* ------------------------------ USERS API -------------------------------- */

const usersAPI = {
  login: (email, password) => api_users.post("/login", { email, password }),
  register: (username, email, password, language = "el", desired_plan = "free") =>
    api_users.post("/register", { username, email, password, language, desired_plan }),
  logout: () => api_users.post("/logout", getStoredRefreshToken() ? { refresh_token: getStoredRefreshToken() } : {}),
  refresh: (refresh_token) => api_users.post("/refresh", { refresh_token }),
  logoutAll: () => api_users.post("/logout_all"),
  me: () => api_users.get("/current_user"),
  getCurrentUser: () => api_users.get("/current_user"),
  entitlements: () => api_users.get("/entitlements"),
  adminDashboard: () => api_users.get("/admin_dashboard"),
  addAdmin: (username, email, password) => api_users.post("/add_admin", { username, email, password }),
  requestPasswordReset: (email) => api_users.post("/request_password_reset", { email }),
  confirmPasswordReset: (token, newPassword) =>
    api_users.post("/confirm_password_reset", { token, new_password: newPassword }),
  // Backward-compatible alias
  resetPassword: (token, new_password) => api_users.post("/confirm_password_reset", { token, new_password }),
  requestTrialRenewal: (email) => api_users.post("/request_trial_renewal", { email }),
  paymentHistory: () => api_users.get("/payment-history"),
  cancelSubscription: (payload = {}) => api_users.post("/cancel-subscription", payload),
  getAuthProviders: () => api_users.get("/auth/providers"),

  googleLogin: ({ token, desired_plan = "free" }) => api_users.post("/login/google", { token, desired_plan }),
  appleLogin: ({ id_token, desired_plan = "free", user = null, email = null, is_private_email = false }) =>
    api_users.post("/login/apple", { id_token, desired_plan, user, email, is_private_email }),
  facebookLogin: ({ token, desired_plan = "free" }) => api_users.post("/login/facebook", { token, desired_plan }),
  facebookCodeLogin: ({ code, redirect_uri, state, desired_plan = "free" }) =>
    api_users.post("/login/facebook/code", { code, redirect_uri, state, desired_plan }),

  claimGuest: (guest_id, local_state) => api_users.post("/guest/claim", { guest_id, local_state }),
  getUsersStats: () => api_users.get("/admin/users_stats"),
};

/* ---------------------------- HOUSEHOLD API ------------------------------ */

const householdAPI = {
  getProfile: () => api_household.get("/profile"),
  saveProfile: (data) => api_household.post("/profile", data),
  getDietTypes: () => api_household.get("/diet_types"),
  getCountryRecipes: () => api_household.get("/country_recipes"),
  getReligiousPreferences: () => api_household.get("/religious_preferences"),
  getFoodTypes: () => api_household.get("/food_types"),
  getMealTypes: () => api_household.get("/meal_types"),
  getSeasons: () => api_household.get("/seasons"),
};

/* ------------------------------ INVENTORY -------------------------------- */

const inventoryAPI = {
  list: () => api_inventory.get("/"),
  addOrUpdate: (data) => api_inventory.post("/", data),
  check: () => api_inventory.get("/check"),
};

/* ------------------------------ RECIPES ---------------------------------- */

const recipesAPI = {
  list: (lang = i18n.language || "en") => api_recipes.get("/list", { params: { lang: toApiLang(lang) } }),

  getOne: (id, { params = {} } = {}) => {
    const newParams = { ...params, lang: toApiLang(params.lang ?? (i18n.language || "en")) };
    return api_recipes.get(`/${id}`, { params: newParams });
  },

  getOneBySlug: (slug, { params = {} } = {}) => {
    const newParams = { ...params, lang: toApiLang(params.lang ?? (i18n.language || "en")) };
    return api_recipes.get(`/slug/${slug}`, { params: newParams });
  },

  getBySlug: (slug, { params = {} } = {}) => {
    const safeSlug = encodeURIComponent(String(slug || ""));
    const newParams = {
      ...params,
      lang: toApiLang(params.lang ?? (i18n.language || "en")),
    };

    return api_recipes.get(`/slug/${safeSlug}`, { params: newParams });
  },

  add: (data, lang = i18n.language || "en") => api_recipes.post("/add", { ...data, lang: toApiLang(lang) }),
  update: (recipeId, recipeData, lang = i18n.language || "en") =>
    api_recipes.post(`/update/${recipeId}`, { ...recipeData, lang: toApiLang(lang) }),
  delete: (recipeId, lang = i18n.language || "en") =>
    api_recipes.post(`/delete/${recipeId}`, { lang: toApiLang(lang) }),

  findByIngredients: (ingredients, lang = i18n.language || "en", country = 1, household_members = 1, max_missing = 2) =>
    api_recipes.post("/find_by_ingredients", {
      ingredients,
      lang: toApiLang(lang),
      country_id: country,
      household_members,
      max_missing,
    }),

  findByName: (name, lang = i18n.language || "en") =>
    api_recipes.get("/search", { params: { query: name, lang: toApiLang(lang) } }),

  getAllGenericProducts: (lang = i18n.language || "en") =>
    api_generic_products.get("/", { params: { lang: toApiLang(lang) } }),

  search: (params = {}) => {
    const paramsWithLang = { ...params, lang: toApiLang(params.lang ?? (i18n.language || "en")) };
    return api_recipes.get("/search", {
      params: Object.fromEntries(Object.entries(paramsWithLang).filter(([_, v]) => v !== undefined && v !== null && v !== "")),
    });
  },

  budgetSearch: async (payload = {}) => {
    try {
      await initGuestOnce(api_users);
    } catch (_err) {
      // Fallback guest tracking on the page handles this gracefully.
    }
    const params = Object.fromEntries(
      Object.entries({
        ...payload,
        lang: backendLangFromI18n(payload.lang ?? (i18n.language || "en")),
      }).filter(([_, value]) => value !== undefined && value !== null && value !== "")
    );
    return api_recipes.get("/budget_recipes", { params });
  },

  uploadImage: (formData, lang = i18n.language || "en") =>
    api_recipes.post("/upload_image", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      params: { lang: toApiLang(lang) },
    }),

  myList: (lang) => {
    const l = toApiLang(lang ?? (i18n.language || "en"));
    return api_recipes.get("/my_recipes", { params: { lang: l } });
  },

  cardsCostEstimate: (payload) => api_recipes.post("/cards_cost_estimate", payload),
};

/* ------------------------------ MEAL PLAN -------------------------------- */

const mealPlanAPI = {
  viewExisting: (profileId) => api_meal_plan.get(`/view_existing_meal_plan/${profileId}`),
  update: (profileId, data) => api_meal_plan.post(`/update_meal_plan/${profileId}`, data),
  save: (data) => api_meal_plan.post("/save", data),
  getUserProfile: (userId) => api_meal_plan.get(`/get_user_profile`, { params: { user_id: userId } }),
  getSavedMealPlan: (params) => api_meal_plan.get("/get_saved", { params }),

  generate: (profileId, params) => api_meal_plan.get(`/generate_meal_plan/${profileId}`, { params }),

  priceRecipe: (params) => api_meal_plan.get(`/price_recipe`, { params }),
  recomputeDraft: (data) => api_meal_plan.post("/recompute_draft", data),
};


/* ------------------------------ GUEST MEAL PLAN -------------------------------- */

const guestMealPlanAPI = {
  generateGuest: async (params = {}) => {
    await initGuestOnce(api_guest);
    return api_guest_meal_plan.post("/mealplan", { ...params });
  },

  getLatestGuest: async (params = {}) => {
    await initGuestOnce(api_guest);
    return api_guest_meal_plan.get("/mealplan", { params });
  },
};


/* -------------------------- INSTACART API (NEW) --------------------------- */

const instacartAPI = {
  usSupportedLocations: () => api_instacart.get("/us_supported_locations"),

  availableStores: (generic_product_id, params = {}) =>
    api_instacart.get(`/available_stores/${generic_product_id}`, { params }),

  variantsForGeneric: (generic_product_id, params = {}) =>
    api_instacart.get(`/variants_for_generic/${generic_product_id}`, { params }),

  instacartPreview: (payload) => api_instacart.post("/checkout/instacart_preview", payload),

  checkoutInstacart: (payload) => api_instacart.post("/checkout/instacart", payload),

  checkoutInstacartFromList: (payload) => api_instacart.post("/checkout/instacart_from_list", payload),

  instacartNearbyStores: () => api_instacart.get("/instacart/nearby_retailers"),

  instacartClick: (payload) => api_instacart.post("/checkout/instacart_click", payload),
};

/* --------------------- SHOPPING LIST PREVIEW API (NEW) -------------------- */

const shoppingListPreviewAPI = {
  preview: (payload) => api_shopping_preview.post("/preview", payload),
  previewCustom: (payload) => api_shopping_preview.post("/preview_custom", payload),
  previewForRecipe: (payload) => api_shopping_preview.post("/preview_for_recipe", payload),
  // Canonical meal-plan shopping preview path shared with mobile.
  previewFromMealPlan: (payload) => api_shopping_list.post("/preview_from_meal_plan", payload),
  recalculate: (payload) => api_shopping_preview.post("/recalculate", payload),
  remainingCost: (lang) => api_shopping_preview.get("/remaining_cost", { params: { lang } }),
  krogerCartItemsFromList: (payload) => api_shopping_preview.post("/kroger/cart_items_from_list", payload),

  saveTemp: (preview, lang = "gr") =>
    api_shopping_preview.post("/save_temp", { preview }, { params: { lang } }),

  loadTemp: (lang = "gr") =>
    api_shopping_preview.get("/load_temp", { params: { lang } }),
};

/* ----------------------------- SHOPPING LIST ------------------------------ */
/* Backwards-compatible: κρατάμε τα ίδια function names που καλούσαν τα components,
   αλλά όπου χρειάζεται “δείχνουμε” στο νέο preview router ή instacart router. */

const shoppingListAPI = {
  // CRUD / lists
  generate: () => api_shopping_list.post("/generate"),
  updateItem: (item_id, quantity_purchased) => api_shopping_list.post("/update_item", { item_id, quantity_purchased }),
  updateItemProgress: (item_id, payload) => api_shopping_list.patch(`/items/${item_id}`, payload),
  updateStore: (item_id, product_variant_id) => api_shopping_list.post("/update_store", { item_id, product_variant_id }),
  // Canonical selection endpoint: keep frontend on the underscore route.
  updateItemSelection: (item_id, payload) => api_shopping_list.patch(`/items/${item_id}/selection`, payload),
  updateStoreContext: (payload) => api_shopping_list.post("/context/store", payload),
  getSelectionGenericProducts: (params = {}) => api_shopping_list.get("/generic_products", { params }),
  getSelectionVariants: (generic_product_id, params = {}) =>
    api_shopping_list.get(`/generic_products/${generic_product_id}/variants`, { params }),

  list: (country_id = 1, lang = "en") =>
    api_shopping_list.get("/", { params: { country_id, lang } }),

  getById: (id) => api_shopping_list.get(`/shopping_list/${id}`),
  getUserLists: () => api_shopping_list.get("/lists"),
  listAll: () => api_shopping_list.get("/lists"),
  aggregate: (params) => api_shopping_list.get("/aggregate", { params }),

  addItem: (data) => api_shopping_list.post("/", data),
  createForRecipe: (recipe_id, household_members = 1) => api_shopping_list.post("/create_for_recipe", { recipe_id, household_members }),

  createRecipeShoppingListSafe: (recipeId, members) =>
    api_shopping_list.post("/create_recipe_safe", { recipe_id: recipeId, household_members: members }),

  saveSelected: (payload) => api_shopping_list.post("/save_selected", payload),
  resolveList: (payload) => api_shopping_list.post("/resolve_list", payload),
  lockListToCurrentBest: (payload) => api_shopping_list.post("/lock", payload),
  saveList: (payload) => api_shopping_list.post("/lists/save", payload),
  // TODO(deprecation): primary-list actions still live behind /api/shopping_lists/*
  // aliases until a canonical shopping-list contract is defined for them.
  setPrimary: (listId, is_primary = true) =>
    api_shopping_lists.post(`/${listId}/set_primary`, { is_primary }),
  clearPrimary: () => api_shopping_lists.post("/clear_primary", {}),

  // ✅ PREVIEW (moved to /api/shopping_list_preview)
  preview: (payload) => shoppingListPreviewAPI.preview(payload),
  previewCustom: (payload) => shoppingListPreviewAPI.previewCustom(payload),
  previewFromMealPlan: (payload) => shoppingListPreviewAPI.previewFromMealPlan(payload),
  saveFromMealPlan: (payload) => api_shopping_list.post("/save_from_meal_plan", payload),
  saveCustom: (payload) => api_shopping_list.post("/save_custom", payload),
  addCustomItem: (listId, payload) => api_shopping_list.post(`/${listId}/items`, payload),
  patchCustomItem: (itemId, payload) => api_shopping_list.patch(`/items/${itemId}`, payload),

  getGuestActive: ({ source = "meal_plan", lang = "gr" } = {}) =>
    api_shopping_list.get("/guest_shopping_list/active", {
      params: { source, lang },
    }),
  getGuestLists: (lang = "gr") =>
    api_shopping_list.get("/guest_shopping_list/lists", { params: { lang } }),


  // ✅ IMPORTANT: αυτό είναι το κρίσιμο που σε έσπαγε
  // old signature supported:
  previewForRecipe: (recipe_id_or_payload, household_members = 1, country_id = 1, lang = "en") => {
    // allow both: previewForRecipe(payload) OR previewForRecipe(recipe_id, members, country, lang)
    const payload =
      typeof recipe_id_or_payload === "object"
        ? recipe_id_or_payload
        : { recipe_id: recipe_id_or_payload, household_members, country_id, lang };
    return shoppingListPreviewAPI.previewForRecipe(payload);
  },

  recalculate: (payload) => shoppingListPreviewAPI.recalculate(payload),
  getRemainingCost: (lang) => shoppingListPreviewAPI.remainingCost(lang),

  // ✅ INSTACART (moved to /api/instacart)
  getUSSupportedLocations: () => instacartAPI.usSupportedLocations(),
  availableStores: (generic_product_id, params = {}) => instacartAPI.availableStores(generic_product_id, params),
  getAvailableStores: (generic_product_id, params = {}) => instacartAPI.availableStores(generic_product_id, params),

  instacartPreview: (payload) => instacartAPI.instacartPreview(payload),
  checkoutInstacart: (payload) => instacartAPI.checkoutInstacart(payload),
  checkoutInstacartFromList: (payload) => instacartAPI.checkoutInstacartFromList(payload),
  instacartNearbyStores: () => instacartAPI.instacartNearbyStores(),
  instacartClick: (payload) => instacartAPI.instacartClick(payload),
};

const guestShoppingListAPI = {
  listAll: (lang = "gr") =>
    api_shopping_list.get("/guest_shopping_list/lists", { params: { lang } }),
};

/* -------------------------- MISC / REMAINING APIs ------------------------ */

const productsAPI = {
  list: () => api_products.get("/"),
  add: (data) => api_products.post("/", data),
  update: (id, data) => api_products.put(`/${id}`, data),
  get: (id) => api_products.get(`/${id}`),
  search: (query) => api_products.get("/search", { params: { query } }),
};

const pricingAPI = {
  cheapest: (recipeId) => api_pricing.get("/cheapest", { params: { recipe_id: recipeId } }),
};

const favoritesAPI = {
  add: (data) => api_favorites.post("/add", data),
  remove: (recipe_id) => api_favorites.delete(`/remove/${recipe_id}`),
  list: () => api_favorites.get("/list"),
};


const appReviewsAPI = {
  list: (params = {}) => api_reviews.get("/", { params }),
  add: (data) => api_reviews.post("/", data),
  stats: () => api_reviews.get("/stats"),
};

const recipeReviewsAPI = {
  list: (recipeId) => api_recipe_reviews.get(`${recipeId}`),
  add: (recipeId, data) => api_recipe_reviews.post(`${recipeId}`, data),
  stats: (recipeId) => api_recipe_reviews.get(`${recipeId}/stats`),
};

const genericProductsAPI = {
  list: () => api_generic_products.get("/"),
  add: (data) => api_generic_products.post("/", data),
  update: (id, data) => api_generic_products.put(`/${id}`, data),
  get: (id) => api_generic_products.get(`/${id}`),
  delete: (id) => api_generic_products.delete(`/${id}`),
  suggest: (data) => api_generic_products.post("/suggest", data),
};

const variantsAPI = {
  list: () => api_variants.get("/"),
  add: (data) => api_variants.post("/", data),
  update: (id, data) => api_variants.put(`/${id}`, data),
};

const stripeAPI = {
  createCheckoutSession: (payload) => api_stripe.post("/create_checkout_session", payload),
  cancelSubscription: (payload = {}) => api_stripe.post("/cancel_subscription", payload),
  createBillingPortalSession: (payload = {}) => api_stripe.post("/create_billing_portal_session", payload),
  webhook: (data, headers = {}) => api_stripe.post("/webhook", data, { headers }),
};

const publicAPI = {
  getPricingCatalog: (countryId) => api_public.get("/pricing-catalog", { params: { country_id: countryId } }),
};

const priceAPI = {
  getDeals: (limit = 20, params = {}) => api_price.get("/deals", { params: { limit, ...params } }),
  getVariantChanges: (variantIds = []) =>
    api_price.get("/variants/changes", { params: { variant_ids: variantIds } }),
  getVariantHistory: (variantId, days) =>
    api_price.get(`/variant/${variantId}/history`, { params: { days } }),
  getWatchlist: () => api_price.get("/watchlist"),
  updateWatchlist: (payload) => api_price.post("/watchlist", payload),
  getAlerts: () => api_price.get("/alerts"),
  createAlert: (payload) => api_price.post("/alerts", payload),
  getSmartBasketSavings: (params = {}) => api_price.get("/smart-basket-savings", { params }),
};

const smartKitchenAPI = {
  getDashboard: (params = {}) => api_smart_kitchen.get("/dashboard", { params }),
  getPantryAwarePreview: (payload = {}) => api_smart_kitchen.post("/pantry_aware_preview", payload),
  getMealPlanPantryIntelligence: (mealPlanId) => api_smart_kitchen.get(`/meal_plans/${mealPlanId}/pantry_intelligence`),
  getMealPlanRecommendations: (mealPlanId) => api_smart_kitchen.get(`/meal_plans/${mealPlanId}/recommendations`),
  previewSplitMeal: (payload = {}) => api_smart_kitchen.post("/split_meals/preview", payload),
  getSplitOptions: (itemId) => api_smart_kitchen.get(`/meal_plan_items/${itemId}/split_options`),
  saveSplitOptions: (itemId, payload = {}) => api_smart_kitchen.put(`/meal_plan_items/${itemId}/split_options`, payload),
  deleteSplitOption: (itemId, optionId) => api_smart_kitchen.delete(`/meal_plan_items/${itemId}/split_options/${optionId}`),
  previewSavedSplitOptions: (itemId) => api_smart_kitchen.get(`/meal_plan_items/${itemId}/split_options/preview`),
  getSplitOptionSuggestions: (itemId) => api_smart_kitchen.get(`/meal_plan_items/${itemId}/split_options/suggestions`),
};

const followedProductsAPI = {
  list: () => api_followed_products.get(""),
  follow: (payload) => api_followed_products.post("", payload),
  unfollow: (id) => api_followed_products.delete(`/${id}`),
};

const followedGenericAPI = {
  list: (params = {}) => api_followed_generic.get("", {
    params: {
      country_id: params.country_id ?? getCurrentCountryId(),
      ...params,
    },
  }),
  follow: (payload) => api_followed_generic.post("", {
    ...payload,
    country_id: payload?.country_id ?? getCurrentCountryId(),
  }),
  unfollow: (genericProductId, params = {}) => api_followed_generic.delete(`/${genericProductId}`, {
    params: {
      country_id: params.country_id ?? getCurrentCountryId(),
      ...params,
    },
  }),
};

const storesAPI = {
  search: ({ q, state, limit, country_id } = {}) =>
    api_stores.get("/search", {
      params: {
        q: q || "",
        state: state || "",
        limit: limit || 20,
        country_id: country_id ?? "",
      },
    }),
  near: ({ zip, city, state, limit } = {}) => api_stores.get("/near", { params: { zip, city, state, limit } }),
};

const adminAPI = {
  growthSummary: (days = 30) =>
    api_admin.get("/growth-summary", { params: { days } }),
};

const krogerAPI = {
  start: (redirectTo) => {
    const p = new URLSearchParams();
    if (redirectTo) p.set("redirect", redirectTo);
    window.location.href = `/api/oauth/kroger/start?${p.toString()}`;
  },
  disconnect: () => api_kroger.post("/oauth/kroger/disconnect"),
  setStore: (locationId) => api_kroger.post("/kroger/store/select", { locationId }),
  addToCart: ({ locationId, items }) => api_kroger.post("/kroger/cart/add", { locationId, items }),
  cartDeeplink: ({ locationId, items }) => api_kroger.post("/kroger/cart/deeplink", { locationId, items }),
  cartPayloadFromList: (locationId) => api_shopping_list.get("/cart_payload", { params: { location_id: locationId } }),
};

/* ------------------------------ EXPORTS ---------------------------------- */

const apiExports = {
  users: usersAPI,
  household: householdAPI,
  recipes: recipesAPI,
  inventory: inventoryAPI,
  mealPlan: mealPlanAPI,
  shoppingList: shoppingListAPI,
  guestShoppingList: guestShoppingListAPI,
  shoppingListPreview: shoppingListPreviewAPI, // (bonus) αν θες direct use
  instacart: instacartAPI, // (bonus) αν θες direct use
  products: productsAPI,
  pricing: pricingAPI,
  favorites: favoritesAPI,
  genericProducts: genericProductsAPI,
  variants: variantsAPI,
  stripe: stripeAPI,
  public: publicAPI,
  price: priceAPI,
  smartKitchen: smartKitchenAPI,
  followedProducts: followedProductsAPI,
  followedGeneric: followedGenericAPI,
  appReviews: appReviewsAPI,
  recipeReviews: recipeReviewsAPI,
  stores: storesAPI,
  admin: adminAPI,
  kroger: krogerAPI,
  guest: guestAPI,
  guestMealPlan: guestMealPlanAPI,
  feedback: feedbackAPI,
  getGuestId,
  setGuestId,
  ensureGuestId,
  getAuthTokenFromPayload,
  persistAuthToken,
  clearAuthToken,

};

export {
  usersAPI,
  householdAPI,
  recipesAPI,
  inventoryAPI,
  mealPlanAPI,
  shoppingListAPI,
  guestShoppingListAPI,
  shoppingListPreviewAPI,
  instacartAPI,
  productsAPI,
  pricingAPI,
  favoritesAPI,
  genericProductsAPI,
  variantsAPI,
  stripeAPI,
  publicAPI,
  priceAPI,
  smartKitchenAPI,
  followedProductsAPI,
  followedGenericAPI,
  appReviewsAPI,
  recipeReviewsAPI,
  storesAPI,
  adminAPI,
  krogerAPI,
  guestAPI,
  guestMealPlanAPI,
  feedbackAPI,
  getGuestId,
  setGuestId,
  ensureGuestId,
  getAuthTokenFromPayload,
  getRefreshTokenFromPayload,
  persistAuthToken,
  persistSessionTokens,
  clearAuthToken,

};

export default apiExports;
